import { Suspense, useCallback, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';
import ErrorFallback from '@/pages/ErrorFallback/ErrorFallback';
import Footer from '@/components/base/Footer/Footer';
import Header from '@/components/base/Header/Header';
import useChatbotStore from '@/components/Chatbot/store/useChatbotStore';
import CookieConsentBanner from '@/components/consent/CookieConsentBanner/CookieConsentBanner';
import { useAuthContext } from '@/context/AuthContext';
import SharedViewDialog from '@/components/filter/SharedViewDialog/SharedViewDialog';
import useApplySharedView from '@/hooks/useApplySharedView';
import useApplyDefaultFilter from '@/hooks/useApplyDefaultFilter';
import { Filter } from '@cubejs-client/core';
import useUserProfile from '@/hooks/useUserProfile';
import AIButton from '@/storybook/components/AIButton/AIButton';
import ErrorBoundaryMorePrecised from '@/storybook/components/ErrorBoundaryMorePrecised/ErrorBoundaryMorePrecised';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import styles from './Layout.module.css';
import PageBanner from './PageBanner/PageBanner';
import { AI_BUTTON_WHITELISTED_USER_IDS, COUNTRIES_WITH_HIDDEN_AI_BUTTON } from './aiButtonAccess.constants';

const Layout = () => {
  const user = useUserProfile();
  const { userInfo } = useAuthContext();
  const { isLoading: isApplyingSharedView, sharedViewDialogProps } = useApplySharedView();
  const { isLoading: isApplyingDefaultFilter } = useApplyDefaultFilter();
  const [tierFilterForHeader, setTierFilterForHeader] = useState<Filter>();
  const [ppocFilterForHeader, setPpocFilterForHeader] = useState<Filter>();
  const isDrawerOpen = useChatbotStore(state => state.isDrawerOpen);
  const setDrawerOpen = useChatbotStore(state => state.setDrawerOpen);

  const handleAIButtonClick = useCallback(() => {
    setDrawerOpen(true);
  }, [setDrawerOpen]);

  const context = useMemo(
    () => ({
      setTierFilterForHeader,
      setPpocFilterForHeader,
      ppocFilterForHeader,
      tierFilterForHeader,
    }),
    [ppocFilterForHeader, tierFilterForHeader],
  );

  const shouldShowAIButton = useMemo(() => {
    if (!userInfo) {
      return false;
    }

    const isWhitelistedUser =
      userInfo.username && AI_BUTTON_WHITELISTED_USER_IDS.has(userInfo.username.trim().toLowerCase());

    return (
      isWhitelistedUser ||
      !userInfo.countries?.length ||
      !userInfo.countries.some(country => COUNTRIES_WITH_HIDDEN_AI_BUTTON.has(country.toUpperCase()))
    );
  }, [userInfo]);

  const showFilterBar = user?.disclaimerRead;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <CookieConsentBanner />
      <Header tierFilter={tierFilterForHeader} ppocFilter={ppocFilterForHeader} />
      <main className={styles.main}>
        <PageBanner />
        {isApplyingSharedView || isApplyingDefaultFilter ? (
          <LoadingSpinner />
        ) : (
          <Suspense fallback={<LoadingSpinner />}>
            <ErrorBoundaryMorePrecised>
              <Outlet context={context} />
            </ErrorBoundaryMorePrecised>
          </Suspense>
        )}
      </main>
      {showFilterBar && <Footer />}
      {sharedViewDialogProps && <SharedViewDialog {...sharedViewDialogProps} />}
      {shouldShowAIButton && !isDrawerOpen && <AIButton onClick={handleAIButtonClick} />}
    </ErrorBoundary>
  );
};

export default Layout;
